import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import ThinHeader from '../components/ThinHeader';
import {
  listAdminUsers,
  updateAdminUserProfile,
  setAdminUserActiveStatus,
  resetAdminUserPassword,
  listAdminOpenGames,
  setAdminGameAuditLog,
  getAdminGameAuditLog,
  clearAdminGameAuditLog,
  getAdminDirectMessageAudit,
  clearAdminChatHistory,
  getAdminStatsAudit,
  closeAdminGame,
  reopenAdminGame,
  deleteAdminGameRecord,
  createRegistrationInvite,
  getRegistrationSettings,
  updateRegistrationSettings,
  getBattleCrySettings,
  updateBattleCrySettings,
} from '../api/adminApi';
import { useAuth } from '../context/AuthContext';
import { BATTLE_CRY_RANKS } from '../constants/battleCryRanks';

const ROLE_OPTIONS = ['player', 'moderator', 'stickler', 'admin'];
const emptyRankMap = () => Object.fromEntries(BATTLE_CRY_RANKS.map((rank) => [rank, '']));
const emptyUserBattleCries = () => ({ single: emptyRankMap(), quad: emptyRankMap() });

const formatRelativeTime = (dateInput) => {
  if (!dateInput) return 'Never';
  const ts = new Date(dateInput).getTime();
  if (!Number.isFinite(ts)) return 'Unknown';

  const deltaSec = Math.max(0, Math.floor((Date.now() - ts) / 1000));
  if (deltaSec < 60) return `${deltaSec}s ago`;
  if (deltaSec < 3600) return `${Math.floor(deltaSec / 60)}m ago`;
  if (deltaSec < 86400) return `${Math.floor(deltaSec / 3600)}h ago`;
  return `${Math.floor(deltaSec / 86400)}d ago`;
};

const toUserDraft = (u = {}) => ({
  displayName: u.displayName || '',
  username: u.username || '',
  email: u.email || '',
  role: u.role || 'player',
  battleCries: {
    single: { ...emptyRankMap(), ...(u.battleCries?.single || {}) },
    quad: { ...emptyRankMap(), ...(u.battleCries?.quad || {}) },
  },
});

const AdminPage = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [users, setUsers] = useState([]);
  const [openGames, setOpenGames] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [editingUserId, setEditingUserId] = useState(null);
  const [resettingPasswordUserId, setResettingPasswordUserId] = useState(null);
  const [editModalUser, setEditModalUser] = useState(null);
  const [editDraft, setEditDraft] = useState({
    displayName: '',
    username: '',
    email: '',
    role: 'player',
    battleCries: emptyUserBattleCries(),
  });
  const [showArchivedUsers, setShowArchivedUsers] = useState(false);
  const [closingGameId, setClosingGameId] = useState(null);
  const [reopeningGameId, setReopeningGameId] = useState(null);
  const [deletingGameId, setDeletingGameId] = useState(null);
  const [togglingAuditGameId, setTogglingAuditGameId] = useState(null);
  const [viewingAuditGameId, setViewingAuditGameId] = useState(null);
  const [clearingAuditGameId, setClearingAuditGameId] = useState(null);
  const [showFinishedGames, setShowFinishedGames] = useState(false);
  const [auditLogModal, setAuditLogModal] = useState({
    open: false,
    gameId: null,
    roomCode: '',
    auditLogEnabled: false,
    lines: [],
  });
  const [statsAuditLoading, setStatsAuditLoading] = useState(false);
  const [statsAuditResult, setStatsAuditResult] = useState(null);
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteExpiresIn, setInviteExpiresIn] = useState('7d');
  const [inviteLink, setInviteLink] = useState('');
  const [creatingInvite, setCreatingInvite] = useState(false);
  const [registrationMode, setRegistrationMode] = useState('open');
  const [savingRegistrationMode, setSavingRegistrationMode] = useState(false);
  const [usersSectionOpen, setUsersSectionOpen] = useState(false);
  const [showUserIds, setShowUserIds] = useState(false);
  const [userSearch, setUserSearch] = useState('');
  const [betaInvitesSectionOpen, setBetaInvitesSectionOpen] = useState(false);
  const [editPasswordDraft, setEditPasswordDraft] = useState({ newPassword: '', confirmPassword: '' });
  const [editPasswordNotice, setEditPasswordNotice] = useState('');
  const [editPasswordError, setEditPasswordError] = useState('');
  const [centerCriesSectionOpen, setCenterCriesSectionOpen] = useState(false);
  const [loungeGamesSectionOpen, setLoungeGamesSectionOpen] = useState(false);
  const [battleCrySettings, setBattleCrySettings] = useState({
    oneOfKind: '',
    twoOfKind: '',
    threeOfKind: '',
    fourOfKind: '',
    bowel: '',
    oneByRank: emptyRankMap(),
    fourByRank: emptyRankMap(),
  });
  const [savingBattleCrySettings, setSavingBattleCrySettings] = useState(false);
  const [dmAuditSectionOpen, setDmAuditSectionOpen] = useState(false);
  const [dmAuditLoading, setDmAuditLoading] = useState(false);
  const [dmAuditRows, setDmAuditRows] = useState([]);
  const [dmAuditFilters, setDmAuditFilters] = useState({ roomCode: '', userId: '', limit: 300 });
  const [clearingChatScope, setClearingChatScope] = useState('');

  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      setError('');
      setSuccessMessage('');
      const [usersRes, gamesRes, registrationRes, battleCryRes] = await Promise.all([
        listAdminUsers(showArchivedUsers),
        listAdminOpenGames(),
        getRegistrationSettings(),
        getBattleCrySettings(),
      ]);
      const nextUsers = usersRes.data.users || [];
      setUsers(nextUsers);
      setOpenGames(gamesRes.data.openGames || []);
      setRegistrationMode(registrationRes.data?.mode || 'open');
      const loadedSettings = battleCryRes.data?.settings || {
        oneOfKind: '',
        twoOfKind: '',
        threeOfKind: '',
        fourOfKind: '',
        bowel: '',
      };
      setBattleCrySettings({
        ...loadedSettings,
        oneByRank: { ...emptyRankMap(), ...(loadedSettings.oneByRank || {}) },
        fourByRank: { ...emptyRankMap(), ...(loadedSettings.fourByRank || {}) },
      });
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to load admin data');
    } finally {
      setLoading(false);
    }
  }, [showArchivedUsers]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  useEffect(() => {
    const timer = setInterval(async () => {
      try {
        const gamesRes = await listAdminOpenGames();
        setOpenGames(gamesRes.data.openGames || []);
      } catch {
        // Ignore transient polling failures; manual refresh remains available.
      }
    }, 3500);

    return () => clearInterval(timer);
  }, []);

  const openEditUserModal = (targetUser) => {
    setEditModalUser(targetUser);
    setEditDraft(toUserDraft(targetUser));
    setEditPasswordDraft({ newPassword: '', confirmPassword: '' });
    setEditPasswordNotice('');
    setEditPasswordError('');
    setSuccessMessage('');
    setError('');
  };

  const closeEditUserModal = () => {
    setEditModalUser(null);
    setEditDraft({
      displayName: '',
      username: '',
      email: '',
      role: 'player',
      battleCries: emptyUserBattleCries(),
    });
    setEditPasswordDraft({ newPassword: '', confirmPassword: '' });
    setEditPasswordNotice('');
    setEditPasswordError('');
  };

  const handleEditDraftFieldChange = (key, value) => {
    setEditDraft((prev) => ({ ...prev, [key]: value }));
  };

  const handleEditDraftBattleCryChange = (variant, rank, value) => {
    setEditDraft((prev) => ({
      ...prev,
      battleCries: {
        ...(prev.battleCries || emptyUserBattleCries()),
        [variant]: {
          ...((prev.battleCries && prev.battleCries[variant]) || emptyRankMap()),
          [rank]: value,
        },
      },
    }));
  };

  const handleSaveUserProfile = async () => {
    try {
      if (!editModalUser?.id) return;
      setEditingUserId(editModalUser.id);
      setError('');
      const { data } = await updateAdminUserProfile(editModalUser.id, {
        displayName: editDraft.displayName,
        username: editDraft.username,
        email: editDraft.email,
        role: editDraft.role,
        battleCries: editDraft.battleCries,
      });
      const updated = data?.user;
      if (updated) {
        setUsers((prev) => prev.map((u) => (u.id === updated.id ? updated : u)));
      }
      setEditPasswordNotice('');
      closeEditUserModal();
    } catch (err) {
      setError(err.response?.data?.error || 'Could not update user profile');
    } finally {
      setEditingUserId(null);
    }
  };

  const handleAdminResetPassword = async () => {
    if (!editModalUser?.id) return;
    const newPassword = editPasswordDraft.newPassword || '';
    const confirmPassword = editPasswordDraft.confirmPassword || '';
    const targetLabel = editDraft.displayName || editDraft.username || editDraft.email || 'this user';

    setEditPasswordError('');
    setEditPasswordNotice('');
    setError('');
    setSuccessMessage('');

    if (newPassword.length < 8) {
      setEditPasswordError('New password must be at least 8 characters');
      return;
    }

    if (newPassword !== confirmPassword) {
      setEditPasswordError('Password confirmation does not match');
      return;
    }

    const shouldProceed = window.confirm(`Reset password for ${targetLabel}?`);
    if (!shouldProceed) return;

    try {
      setResettingPasswordUserId(editModalUser.id);
      await resetAdminUserPassword(editModalUser.id, newPassword);
      setEditPasswordDraft({ newPassword: '', confirmPassword: '' });
      setEditPasswordNotice(`Password reset complete for ${targetLabel}. User must sign in again with the new password.`);
      setSuccessMessage(`Password reset complete for ${targetLabel}.`);
    } catch (err) {
      setEditPasswordError(err.response?.data?.error || 'Could not reset user password');
    } finally {
      setResettingPasswordUserId(null);
    }
  };

  const handleSetUserActive = async (userId, isActive) => {
    try {
      setEditingUserId(userId);
      const { data } = await setAdminUserActiveStatus(userId, isActive);
      const updated = data?.user;
      if (updated) {
        if (!showArchivedUsers && !updated.isActive) {
          setUsers((prev) => prev.filter((u) => u.id !== userId));
        } else {
          setUsers((prev) => prev.map((u) => (u.id === userId ? updated : u)));
        }
      }
    } catch (err) {
      setError(err.response?.data?.error || 'Could not update user status');
    } finally {
      setEditingUserId(null);
    }
  };

  const handleCloseGame = async (gameId) => {
    try {
      setClosingGameId(gameId);
      await closeAdminGame(gameId);
      const { data } = await listAdminOpenGames();
      setOpenGames(data.openGames || []);
    } catch (err) {
      setError(err.response?.data?.error || 'Could not close game');
    } finally {
      setClosingGameId(null);
    }
  };

  const handleReopenGame = async (gameId) => {
    try {
      setReopeningGameId(gameId);
      setError('');
      await reopenAdminGame(gameId);
      const { data } = await listAdminOpenGames();
      setOpenGames(data.openGames || []);
    } catch (err) {
      setError(err.response?.data?.error || 'Could not reopen game');
    } finally {
      setReopeningGameId(null);
    }
  };

  const handleDeleteGameRecord = async (gameId) => {
    try {
      setDeletingGameId(gameId);
      await deleteAdminGameRecord(gameId);
      setOpenGames((prev) => prev.filter((g) => g.id !== gameId));
    } catch (err) {
      setError(err.response?.data?.error || 'Could not delete game record');
    } finally {
      setDeletingGameId(null);
    }
  };

  const handleToggleGameAuditLog = async (gameId, enabled) => {
    try {
      setTogglingAuditGameId(gameId);
      setError('');
      const { data } = await setAdminGameAuditLog(gameId, enabled);
      setOpenGames((prev) => prev.map((g) => (
        g.id === gameId
          ? { ...g, auditLogEnabled: !!data?.auditLogEnabled }
          : g
      )));
    } catch (err) {
      setError(err.response?.data?.error || 'Could not update audit log setting');
    } finally {
      setTogglingAuditGameId(null);
    }
  };

  const handleViewAuditLog = async (gameId, roomCode) => {
    try {
      setViewingAuditGameId(gameId);
      setError('');
      const { data } = await getAdminGameAuditLog(gameId, 500);
      const lines = (data.auditLog || []).map((entry) => {
        const ts = entry.createdAt ? new Date(entry.createdAt).toLocaleTimeString() : '';
        return `${ts ? `[${ts}] ` : ''}${entry.line || ''}`;
      });
      setAuditLogModal({
        open: true,
        gameId,
        roomCode: roomCode || data.roomCode || '',
        auditLogEnabled: !!data.auditLogEnabled,
        lines,
      });
    } catch (err) {
      setError(err.response?.data?.error || 'Could not load audit log');
    } finally {
      setViewingAuditGameId(null);
    }
  };

  const handleClearAuditLog = async () => {
    if (!auditLogModal.gameId) return;
    try {
      setClearingAuditGameId(auditLogModal.gameId);
      setError('');
      await clearAdminGameAuditLog(auditLogModal.gameId);
      setAuditLogModal((prev) => ({ ...prev, lines: [] }));
      setOpenGames((prev) => prev.map((g) => (
        g.id === auditLogModal.gameId
          ? { ...g, auditLogCount: 0 }
          : g
      )));
    } catch (err) {
      setError(err.response?.data?.error || 'Could not clear audit log');
    } finally {
      setClearingAuditGameId(null);
    }
  };

  const handleRunStatsAudit = async () => {
    try {
      setStatsAuditLoading(true);
      setError('');
      const { data } = await getAdminStatsAudit();
      setStatsAuditResult(data);
    } catch (err) {
      setError(err.response?.data?.error || 'Could not run stats audit');
    } finally {
      setStatsAuditLoading(false);
    }
  };

  const visibleAdminGames = showFinishedGames
    ? openGames
    : openGames.filter((g) => g.status !== 'finished');

  const filteredUsers = users.filter((u) => {
    const q = userSearch.trim().toLowerCase();
    if (!q) return true;
    return `${u.displayName || ''} ${u.username || ''} ${u.email || ''} ${u.role || ''} ${u.id || ''}`
      .toLowerCase()
      .includes(q);
  });

  const handleCreateInvite = async (e) => {
    e.preventDefault();
    try {
      setCreatingInvite(true);
      setError('');
      setInviteLink('');

      const payload = {
        expiresIn: inviteExpiresIn || '7d',
      };

      if (inviteEmail.trim()) {
        payload.email = inviteEmail.trim();
      }

      const { data } = await createRegistrationInvite(payload);
      setInviteLink(data.inviteUrl || '');
    } catch (err) {
      setError(err.response?.data?.error || 'Could not create invite link');
    } finally {
      setCreatingInvite(false);
    }
  };

  const handlePublicRegistrationToggle = async (enabled) => {
    try {
      setSavingRegistrationMode(true);
      setError('');
      const { data } = await updateRegistrationSettings({ publicRegistrationEnabled: enabled });
      setRegistrationMode(data.mode || (enabled ? 'open' : 'invite'));
    } catch (err) {
      setError(err.response?.data?.error || 'Could not update registration mode');
    } finally {
      setSavingRegistrationMode(false);
    }
  };

  const handleBattleCrySettingChange = (e) => {
    setBattleCrySettings((prev) => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const handleBattleCryRankChange = (variant, rank, value) => {
    setBattleCrySettings((prev) => ({
      ...prev,
      [variant]: {
        ...(prev[variant] || {}),
        [rank]: value,
      },
    }));
  };

  const handleSaveBattleCrySettings = async (e) => {
    e.preventDefault();
    try {
      setSavingBattleCrySettings(true);
      setError('');
      const { data } = await updateBattleCrySettings(battleCrySettings);
      const nextSettings = data.settings || battleCrySettings;
      setBattleCrySettings({
        ...nextSettings,
        oneByRank: { ...emptyRankMap(), ...(nextSettings.oneByRank || {}) },
        fourByRank: { ...emptyRankMap(), ...(nextSettings.fourByRank || {}) },
      });
    } catch (err) {
      setError(err.response?.data?.error || 'Could not update battle cry settings');
    } finally {
      setSavingBattleCrySettings(false);
    }
  };

  const loadDirectMessageAudit = async () => {
    try {
      setDmAuditLoading(true);
      setError('');
      const { data } = await getAdminDirectMessageAudit({
        roomCode: dmAuditFilters.roomCode.trim().toUpperCase(),
        userId: dmAuditFilters.userId.trim(),
        limit: Number(dmAuditFilters.limit || 300),
      });
      setDmAuditRows(data?.messages || []);
    } catch (err) {
      setError(err.response?.data?.error || 'Could not load direct message audit log');
    } finally {
      setDmAuditLoading(false);
    }
  };

  const handleDmAuditFilterChange = (key, value) => {
    setDmAuditFilters((prev) => ({ ...prev, [key]: value }));
  };

  const handleClearChatHistory = async (scope) => {
    const normalizedScope = `${scope || ''}`.trim();
    if (!normalizedScope) return;

    const confirmMessage = normalizedScope === 'all'
      ? 'Clear all chat history (lounge, DM, and room chats)? This cannot be undone.'
      : `Clear ${normalizedScope.replace('_', ' ')} chat history? This cannot be undone.`;

    if (!window.confirm(confirmMessage)) return;

    try {
      setClearingChatScope(normalizedScope);
      setError('');
      setSuccessMessage('');

      const roomCode = dmAuditFilters.roomCode.trim().toUpperCase();
      let roomId;
      if (normalizedScope.startsWith('room_')) {
        const matchingGame = openGames.find((game) => `${game.roomCode || ''}`.toUpperCase() === roomCode);
        if (!matchingGame?._id) {
          setError('Enter a valid room code from the Lounge Games list before clearing room chat.');
          return;
        }
        roomId = matchingGame._id;
      }

      const { data } = await clearAdminChatHistory({
        scope: normalizedScope,
        roomId,
      });

      setSuccessMessage(
        normalizedScope === 'all'
          ? `Cleared all chat history (${data?.deletedCount || 0} message(s)).`
          : `Cleared ${normalizedScope.replace('_', ' ')} chat history (${data?.deletedCount || 0} message(s)).`
      );
      await loadData();
      await loadDirectMessageAudit();
    } catch (err) {
      setError(err.response?.data?.error || 'Could not clear chat history');
    } finally {
      setClearingChatScope('');
    }
  };

  return (
    <div className="admin-page">
      <ThinHeader
        homeTo="/"
        userLabel={user?.displayName || user?.username || user?.email || 'Player'}
        showAdminLink={user?.role === 'admin'}
      />

      <div className="admin-title-row">
        <h1>Admin Panel</h1>
        <button className="thin-header-btn" onClick={loadData} type="button">Refresh</button>
      </div>

      {error && <p className="error">{error}</p>}
      {successMessage && <p className="admin-global-success">{successMessage}</p>}
      {loading ? <p>Loading admin data...</p> : null}

      <section className="admin-card">
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '0.6rem' }}>
          <h2>User Management</h2>
          <button className="thin-header-btn" type="button" onClick={() => setUsersSectionOpen((prev) => !prev)}>
            {usersSectionOpen ? 'Collapse' : 'Expand'}
          </button>
        </div>
        {usersSectionOpen && (
          <>
            <p className="admin-note">Admins can appoint moderators and sticklers.</p>
            <div className="registration-toggle-row" style={{ marginBottom: '0.65rem' }}>
              <label className="registration-toggle-label">
                <input
                  type="checkbox"
                  checked={showArchivedUsers}
                  onChange={(e) => setShowArchivedUsers(e.target.checked)}
                />
                Show archived users
              </label>
              <label className="registration-toggle-label">
                <input
                  type="checkbox"
                  checked={showUserIds}
                  onChange={(e) => setShowUserIds(e.target.checked)}
                />
                Show user IDs
              </label>
            </div>
            <div className="stats-controls-row" style={{ marginBottom: '0.6rem' }}>
              <input
                type="text"
                value={userSearch}
                onChange={(e) => setUserSearch(e.target.value)}
                placeholder="Search users by name, username, email, role, or id"
              />
            </div>
            <div className="admin-table-wrap">
              <table className="admin-table">
                <thead>
                  <tr>
                    {showUserIds && <th>ID</th>}
                    <th>Name</th>
                    <th>Username</th>
                    <th>Email</th>
                    <th>Role</th>
                    <th>Status</th>
                    <th>Online</th>
                    <th>Last Active</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredUsers.map((u) => (
                    <tr key={u.id}>
                      {showUserIds && <td><small>{u.id}</small></td>}
                      <td>{u.displayName}</td>
                      <td>{u.username}</td>
                      <td>{u.email}</td>
                      <td>{u.role}</td>
                      <td>{u.isActive ? 'Active' : 'Archived'}</td>
                      <td>
                        <span className={`leader-activity-badge ${u.onlineNow ? 'online' : 'offline'}`}>
                          {u.onlineNow ? 'Online now' : 'Offline'}
                        </span>
                      </td>
                      <td>
                        <div>{formatRelativeTime(u.lastActiveAt)}</div>
                        <small className="admin-note">{u.lastActiveAt ? new Date(u.lastActiveAt).toLocaleString() : 'Never connected'}</small>
                      </td>
                      <td>
                        <div style={{ display: 'flex', gap: '0.45rem', flexWrap: 'wrap' }}>
                          <button
                            type="button"
                            className="btn-cancel"
                            disabled={editingUserId === u.id}
                            onClick={() => openEditUserModal(u)}
                            title="Edit user"
                          >
                            ✎
                          </button>
                          <button
                            type="button"
                            className="btn-cancel"
                            disabled={editingUserId === u.id}
                            onClick={() => handleSetUserActive(u.id, !u.isActive)}
                            title={u.isActive ? 'Archive user' : 'Restore user'}
                          >
                            {u.isActive ? '🗄' : '↩'}
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                  {filteredUsers.length === 0 && (
                    <tr>
                      <td colSpan={showUserIds ? 10 : 9}>No users match this search.</td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </>
        )}
      </section>

      {editModalUser && (
        <div className="swap-modal-overlay" role="dialog" aria-modal="true" aria-label="Edit user modal">
          <div className="swap-modal-panel admin-edit-modal" style={{ maxWidth: '900px', width: '95%' }}>
            <h3 className="swap-modal-title">Edit User</h3>
            <p className="admin-note">Moderation editor for profile, role, and personal battle cries.</p>
            <div className="auth-form admin-edit-modal-profile">
              <input
                value={editDraft.displayName}
                onChange={(e) => handleEditDraftFieldChange('displayName', e.target.value)}
                placeholder="Display name"
              />
              <input
                value={editDraft.username}
                onChange={(e) => handleEditDraftFieldChange('username', e.target.value)}
                placeholder="Username"
              />
              <input
                value={editDraft.email}
                onChange={(e) => handleEditDraftFieldChange('email', e.target.value)}
                placeholder="Email"
              />
              <select
                value={editDraft.role}
                onChange={(e) => handleEditDraftFieldChange('role', e.target.value)}
              >
                {ROLE_OPTIONS.map((role) => (
                  <option key={`modal-role-${role}`} value={role}>{role}</option>
                ))}
              </select>
            </div>

            <div className="admin-edit-password-card">
              <h4>Admin Password Reset</h4>
              <p className="admin-note">Set a temporary password for this user. They can change it after signing in.</p>
              <div className="admin-edit-password-grid">
                <input
                  type="password"
                  value={editPasswordDraft.newPassword}
                  onChange={(e) => {
                    setEditPasswordDraft((prev) => ({ ...prev, newPassword: e.target.value }));
                    if (editPasswordError) setEditPasswordError('');
                  }}
                  placeholder="New temporary password"
                />
                <input
                  type="password"
                  value={editPasswordDraft.confirmPassword}
                  onChange={(e) => {
                    setEditPasswordDraft((prev) => ({ ...prev, confirmPassword: e.target.value }));
                    if (editPasswordError) setEditPasswordError('');
                  }}
                  placeholder="Confirm temporary password"
                />
              </div>
              <div className="admin-edit-password-actions">
                <button
                  type="button"
                  className="btn-cancel"
                  onClick={handleAdminResetPassword}
                  disabled={resettingPasswordUserId === editModalUser.id}
                >
                  {resettingPasswordUserId === editModalUser.id ? 'Resetting...' : 'Reset Password'}
                </button>
              </div>
              {editPasswordError && <p className="admin-error-note">{editPasswordError}</p>}
              {editPasswordNotice && <p className="admin-success-note">{editPasswordNotice}</p>}
            </div>

            <div className="battle-cries-panel" style={{ marginTop: '0.75rem' }}>
              <div className="battle-cries-headings">
                <h3>User Battle Cries</h3>
                <p>Edit 1-card and 4-card cry overrides for moderation needs.</p>
              </div>
              <div className="battle-cries-grid battle-cries-grid--header">
                <span>Rank</span>
                <span>User: 1 Card</span>
                <span>User: 4 Cards</span>
              </div>
              {BATTLE_CRY_RANKS.map((rank) => (
                <div className="battle-cries-grid" key={`modal-user-cry-${rank}`}>
                  <strong>{rank}</strong>
                  <input
                    type="text"
                    value={editDraft.battleCries?.single?.[rank] || ''}
                    onChange={(e) => handleEditDraftBattleCryChange('single', rank, e.target.value)}
                    placeholder={`User text for 1 ${rank}`}
                    maxLength={120}
                  />
                  <input
                    type="text"
                    value={editDraft.battleCries?.quad?.[rank] || ''}
                    onChange={(e) => handleEditDraftBattleCryChange('quad', rank, e.target.value)}
                    placeholder={`User text for 4 ${rank}s`}
                    maxLength={120}
                  />
                </div>
              ))}
            </div>

            <div style={{ display: 'flex', gap: '0.6rem', marginTop: '0.9rem', justifyContent: 'flex-end' }}>
              <button type="button" className="btn-cancel" onClick={closeEditUserModal}>Cancel</button>
              <button
                type="button"
                className="btn-play"
                onClick={handleSaveUserProfile}
                disabled={editingUserId === editModalUser.id}
              >
                {editingUserId === editModalUser.id ? 'Saving...' : 'Save User'}
              </button>
            </div>
          </div>
        </div>
      )}

      <section className="admin-card">
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '0.6rem' }}>
          <h2>Direct Message Moderation</h2>
          <button className="thin-header-btn" type="button" onClick={() => setDmAuditSectionOpen((prev) => !prev)}>
            {dmAuditSectionOpen ? 'Collapse' : 'Expand'}
          </button>
        </div>
        {dmAuditSectionOpen && (
          <>
            <p className="admin-note">Review lounge/game direct messages by room or user for moderation.</p>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.45rem', marginBottom: '0.8rem' }}>
              <button className="thin-header-btn" type="button" onClick={() => handleClearChatHistory('lounge')} disabled={!!clearingChatScope}>
                {clearingChatScope === 'lounge' ? 'Clearing...' : 'Clear Lounge Chat'}
              </button>
              <button className="thin-header-btn" type="button" onClick={() => handleClearChatHistory('dm')} disabled={!!clearingChatScope}>
                {clearingChatScope === 'dm' ? 'Clearing...' : 'Clear DM Chat'}
              </button>
              <button className="thin-header-btn" type="button" onClick={() => handleClearChatHistory('room_game')} disabled={!!clearingChatScope}>
                {clearingChatScope === 'room_game' ? 'Clearing...' : 'Clear Room Chat'}
              </button>
              <button className="thin-header-btn" type="button" onClick={() => handleClearChatHistory('all')} disabled={!!clearingChatScope}>
                {clearingChatScope === 'all' ? 'Clearing...' : 'Clear All Chats'}
              </button>
            </div>
            <p className="admin-note">Room clears use the room code typed below. Lounge and DM clears do not need a room code.</p>
            <div className="stats-controls-row" style={{ marginBottom: '0.65rem', display: 'flex', gap: '0.45rem', flexWrap: 'wrap' }}>
              <input
                type="text"
                placeholder="Room code (optional)"
                value={dmAuditFilters.roomCode}
                onChange={(e) => handleDmAuditFilterChange('roomCode', e.target.value)}
              />
              <input
                type="text"
                placeholder="User ID filter (optional)"
                value={dmAuditFilters.userId}
                onChange={(e) => handleDmAuditFilterChange('userId', e.target.value)}
              />
              <input
                type="number"
                min={1}
                max={2000}
                placeholder="Limit"
                value={dmAuditFilters.limit}
                onChange={(e) => handleDmAuditFilterChange('limit', e.target.value)}
              />
              <button className="thin-header-btn" type="button" onClick={loadDirectMessageAudit} disabled={dmAuditLoading}>
                {dmAuditLoading ? 'Loading...' : 'Load DM Audit'}
              </button>
            </div>
            <div className="admin-table-wrap">
              <table className="admin-table">
                <thead>
                  <tr>
                    <th>Time</th>
                    <th>Room</th>
                    <th>Source</th>
                    <th>From</th>
                    <th>To</th>
                    <th>Message</th>
                  </tr>
                </thead>
                <tbody>
                  {dmAuditRows.map((row, idx) => (
                    <tr key={`dm-audit-${row.gameId || 'game'}-${row.createdAt || idx}-${idx}`}>
                      <td>{row.createdAt ? new Date(row.createdAt).toLocaleString() : '-'}</td>
                      <td>{row.roomCode || '-'}</td>
                      <td>{row.source || '-'}</td>
                      <td>
                        <div>{row.senderDisplayName || 'Player'}</div>
                        <small>{row.senderUserId || '-'}</small>
                      </td>
                      <td>
                        <div>{row.recipientDisplayName || 'Player'}</div>
                        <small>{row.recipientUserId || '-'}</small>
                      </td>
                      <td>{row.text || ''}</td>
                    </tr>
                  ))}
                  {dmAuditRows.length === 0 && (
                    <tr>
                      <td colSpan={6}>No direct messages for this filter.</td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </>
        )}
      </section>

      <section className="admin-card">
        <h2>Registration Access</h2>
        <p className="admin-note">Turn public registration off to require invite links during beta.</p>
        <div className="registration-toggle-row">
          <label className="registration-toggle-label">
            <input
              type="checkbox"
              checked={registrationMode === 'open'}
              disabled={savingRegistrationMode}
              onChange={(e) => handlePublicRegistrationToggle(e.target.checked)}
            />
            Public registration enabled
          </label>
          <span className="registration-mode-pill">Mode: {registrationMode}</span>
        </div>
      </section>

      <section className="admin-card">
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '0.6rem' }}>
          <h2>Center Tile Battle Cries</h2>
          <button className="thin-header-btn" type="button" onClick={() => setCenterCriesSectionOpen((prev) => !prev)}>
            {centerCriesSectionOpen ? 'Collapse' : 'Expand'}
          </button>
        </div>
        {centerCriesSectionOpen && (
          <>
            <p className="admin-note">These show in the middle of the play area when someone plays 1, 2, 3, 4 cards of a kind, or when a bowel happens.</p>
            <form className="auth-form" onSubmit={handleSaveBattleCrySettings}>
              <input
                name="oneOfKind"
                value={battleCrySettings.oneOfKind || ''}
                onChange={handleBattleCrySettingChange}
                placeholder="Battle cry for 1 of a kind"
                maxLength={120}
              />
              <input
                name="twoOfKind"
                value={battleCrySettings.twoOfKind || ''}
                onChange={handleBattleCrySettingChange}
                placeholder="Battle cry for 2 of a kind"
                maxLength={120}
              />
              <input
                name="threeOfKind"
                value={battleCrySettings.threeOfKind || ''}
                onChange={handleBattleCrySettingChange}
                placeholder="Battle cry for 3 of a kind"
                maxLength={120}
              />
              <input
                name="fourOfKind"
                value={battleCrySettings.fourOfKind || ''}
                onChange={handleBattleCrySettingChange}
                placeholder="Battle cry for 4 of a kind"
                maxLength={120}
              />
              <input
                name="bowel"
                value={battleCrySettings.bowel || ''}
                onChange={handleBattleCrySettingChange}
                placeholder="Battle cry for a bowel"
                maxLength={120}
              />
              <div className="battle-cries-panel">
                <div className="battle-cries-headings">
                  <h3>Rank Overrides</h3>
                  <p>Optional: set custom center text by rank for 1-card and 4-card plays. Example: 4 x 9's = Kelly Bowel.</p>
                </div>
                <div className="battle-cries-grid battle-cries-grid--header">
                  <span>Rank</span>
                  <span>Center: 1 Card</span>
                  <span>Center: 4 Cards</span>
                </div>
                {BATTLE_CRY_RANKS.map((rank) => (
                  <div className="battle-cries-grid" key={`admin-cry-${rank}`}>
                    <strong>{rank}</strong>
                    <input
                      type="text"
                      value={battleCrySettings.oneByRank?.[rank] || ''}
                      onChange={(e) => handleBattleCryRankChange('oneByRank', rank, e.target.value)}
                      placeholder={`Center text for 1 ${rank}`}
                      maxLength={120}
                    />
                    <input
                      type="text"
                      value={battleCrySettings.fourByRank?.[rank] || ''}
                      onChange={(e) => handleBattleCryRankChange('fourByRank', rank, e.target.value)}
                      placeholder={`Center text for 4 ${rank}s`}
                      maxLength={120}
                    />
                  </div>
                ))}
              </div>
              <button type="submit" disabled={savingBattleCrySettings}>
                {savingBattleCrySettings ? 'Saving...' : 'Save Battle Cries'}
              </button>
            </form>
          </>
        )}
      </section>

      <section className="admin-card">
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '0.6rem' }}>
          <h2>Beta Registration Invites</h2>
          <button className="thin-header-btn" type="button" onClick={() => setBetaInvitesSectionOpen((prev) => !prev)}>
            {betaInvitesSectionOpen ? 'Collapse' : 'Expand'}
          </button>
        </div>
        {betaInvitesSectionOpen && (
          <>
            <p className="admin-note">Use this to email secure invite links when registration is invite-only.</p>
            <form className="auth-form" onSubmit={handleCreateInvite}>
              <input
                name="inviteEmail"
                type="email"
                placeholder="Invite email (optional, locks link to this email)"
                value={inviteEmail}
                onChange={(e) => setInviteEmail(e.target.value)}
              />
              <input
                name="inviteExpires"
                placeholder="Expiry (example: 7d, 24h, 60m)"
                value={inviteExpiresIn}
                onChange={(e) => setInviteExpiresIn(e.target.value)}
              />
              <button type="submit" disabled={creatingInvite}>
                {creatingInvite ? 'Creating...' : 'Create Invite Link'}
              </button>
            </form>
            {inviteLink && (
              <p>
                Invite link: <a href={inviteLink} target="_blank" rel="noreferrer">{inviteLink}</a>
              </p>
            )}
          </>
        )}
      </section>

      <section className="admin-card">
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '0.6rem' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.6rem', flexWrap: 'wrap' }}>
            <h2 style={{ marginBottom: 0 }}>Lounge Games</h2>
            <button className="thin-header-btn" type="button" onClick={handleRunStatsAudit} disabled={statsAuditLoading}>
              {statsAuditLoading ? 'Auditing...' : 'Run Stats Audit'}
            </button>
          </div>
          <button className="thin-header-btn" type="button" onClick={() => setLoungeGamesSectionOpen((prev) => !prev)}>
            {loungeGamesSectionOpen ? 'Collapse' : 'Expand'}
          </button>
        </div>
        {loungeGamesSectionOpen && (
          <>
            <p className="admin-note">Shows all lounge-visible rooms (including finished). Use A/P/W plus names to spot inactive rooms you can close.</p>
            {statsAuditResult && (
              <div className="registration-mode-pill" style={{ marginBottom: '0.8rem', display: 'block' }}>
                Stats audit: {statsAuditResult.summary?.mismatchedUsers || 0} mismatched users, {statsAuditResult.summary?.orphanedEntries || 0} orphaned entries, {statsAuditResult.summary?.roundCount || 0} rounds scanned.
              </div>
            )}
            <div className="registration-toggle-row" style={{ marginBottom: '0.65rem' }}>
              <label className="registration-toggle-label">
                <input
                  type="checkbox"
                  checked={showFinishedGames}
                  onChange={(e) => setShowFinishedGames(e.target.checked)}
                />
                Show finished games
              </label>
            </div>
            <div className="admin-table-wrap">
              <table className="admin-table">
                <thead>
                  <tr>
                    <th>Room</th>
                    <th>Status</th>
                    <th>A / P / W</th>
                    <th>Names</th>
                    <th>Action</th>
                  </tr>
                </thead>
                <tbody>
                  {visibleAdminGames.map((g) => (
                    <tr key={g.id}>
                      <td>
                        <div><strong>{g.roomCode}</strong></div>
                        <div><small>{g.gameName || `Room ${g.roomCode}`}</small></div>
                      </td>
                      <td>
                        {g.status}
                        {g.isPersistent ? ' • Persistent' : ''}
                        {g.tournament?.enabled ? ` • ${g.tournament.mode === 'long-play' ? 'Long-Play Tournament' : 'Live Tournament'}` : ''}
                        {g.tournament?.enabled && g.tournament?.inactivityTimerEnabled
                          ? ` • Timer ${g.tournament?.inactivityTimeoutMinutes || 30}m`
                          : ''}
                        {g.status === 'finished' && g.closeMeta?.source ? ` • Closed: ${g.closeMeta.source}` : ''}
                      </td>
                      <td>
                        <strong>{g.activeUsers ?? 0}</strong> / {g.playerCount ?? 0} / {g.waitlistCount ?? 0}
                      </td>
                      <td>
                        <div>
                          <small><strong>A:</strong> {(g.activeNames || []).join(', ') || 'None'}</small>
                        </div>
                        <div>
                          <small><strong>P:</strong> {(g.playerNames || []).join(', ') || 'None'}</small>
                        </div>
                        <div>
                          <small><strong>W:</strong> {(g.waitlistNames || []).join(', ') || 'None'}</small>
                        </div>
                      </td>
                      <td>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', flexWrap: 'wrap', marginBottom: '0.35rem' }}>
                          <label className="registration-toggle-label" style={{ fontSize: '0.85rem' }}>
                            <input
                              type="checkbox"
                              checked={!!g.auditLogEnabled}
                              disabled={togglingAuditGameId === g.id}
                              onChange={(e) => handleToggleGameAuditLog(g.id, e.target.checked)}
                            />
                            Log game
                          </label>
                          <button
                            className="btn-cancel"
                            type="button"
                            title="View audit log"
                            disabled={viewingAuditGameId === g.id}
                            onClick={() => handleViewAuditLog(g.id, g.roomCode)}
                          >
                            {viewingAuditGameId === g.id ? '...' : '📜'}
                          </button>
                          <small>({g.auditLogCount ?? 0})</small>
                        </div>
                        <div style={{ display: 'flex', gap: '0.45rem', flexWrap: 'wrap' }}>
                          <button
                            className="btn-play"
                            disabled={deletingGameId === g.id}
                            onClick={() => navigate(`/game/${g.id}?view=waiting`)}
                            type="button"
                          >
                            Join Waiting Room
                          </button>
                          <button
                            className="btn-cancel"
                            disabled={closingGameId === g.id || g.status === 'finished' || deletingGameId === g.id || reopeningGameId === g.id}
                            onClick={() => handleCloseGame(g.id)}
                            type="button"
                          >
                            {g.status === 'finished' ? 'Already Finished' : 'Close Game'}
                          </button>
                          {g.status === 'finished' && (
                            <button
                              className="btn-play"
                              disabled={reopeningGameId === g.id || deletingGameId === g.id || closingGameId === g.id}
                              onClick={() => handleReopenGame(g.id)}
                              type="button"
                            >
                              {reopeningGameId === g.id ? 'Reopening...' : 'Reopen Game'}
                            </button>
                          )}
                          <button
                            className="btn-cancel"
                            disabled={deletingGameId === g.id || closingGameId === g.id || reopeningGameId === g.id || g.status !== 'finished'}
                            onClick={() => handleDeleteGameRecord(g.id)}
                            type="button"
                          >
                            {deletingGameId === g.id ? 'Deleting...' : 'Delete Record'}
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                  {visibleAdminGames.length === 0 && (
                    <tr>
                      <td colSpan={5}>No games for this filter.</td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
            {statsAuditResult?.mismatchedUsers?.length > 0 && (
              <div style={{ marginTop: '0.85rem' }}>
                <h3>Audit Mismatches</h3>
                <div className="admin-table-wrap">
                  <table className="admin-table">
                    <thead>
                      <tr>
                        <th>Player</th>
                        <th>Stored Points</th>
                        <th>Expected Points</th>
                        <th>Delta</th>
                      </tr>
                    </thead>
                    <tbody>
                      {statsAuditResult.mismatchedUsers.map((entry) => (
                        <tr key={`audit-${entry.userId}`}>
                          <td>{entry.displayName}</td>
                          <td>{entry.stored?.totalPoints ?? 0}</td>
                          <td>{entry.expected?.totalPoints ?? 0}</td>
                          <td>{entry.delta?.totalPoints ?? 0}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </>
        )}
      </section>

      {auditLogModal.open && (
        <div className="swap-modal-overlay" role="dialog" aria-modal="true" aria-label="Game audit log modal">
          <div className="swap-modal-panel" style={{ maxWidth: '920px', width: '95%' }}>
            <h3 className="swap-modal-title">Game Audit Log - {auditLogModal.roomCode}</h3>
            <p className="admin-note">
              Audit logging is currently {auditLogModal.auditLogEnabled ? 'enabled' : 'disabled'} for this room.
            </p>
            <div
              style={{
                maxHeight: '55vh',
                overflowY: 'auto',
                background: 'rgba(15, 17, 23, 0.65)',
                border: '1px solid rgba(255,255,255,0.12)',
                borderRadius: '8px',
                padding: '0.8rem',
                fontFamily: 'monospace',
                fontSize: '0.9rem',
                lineHeight: 1.35,
                whiteSpace: 'pre-wrap',
              }}
            >
              {auditLogModal.lines.length > 0
                ? auditLogModal.lines.join('\n')
                : 'No audit lines captured yet.'}
            </div>
            <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '0.85rem' }}>
              <button
                type="button"
                className="btn-cancel"
                onClick={handleClearAuditLog}
                disabled={!auditLogModal.gameId || clearingAuditGameId === auditLogModal.gameId}
                style={{ marginRight: '0.5rem' }}
              >
                {clearingAuditGameId === auditLogModal.gameId ? 'Clearing...' : 'Clear Log'}
              </button>
              <button
                type="button"
                className="btn-cancel"
                onClick={() => setAuditLogModal({ open: false, gameId: null, roomCode: '', auditLogEnabled: false, lines: [] })}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminPage;
